/**
 * Identity scope / generation primitive (APP-W01a).
 *
 * Minimal fencing primitive for identity authority:
 *
 * - Every server-confirmed identity commit advances a monotonically
 *   increasing generation and captures the confirmed user/workspace ids.
 * - In-flight identity work (probe fetches, side effects) registers against
 *   the generation it started under; a transition invalidation aborts it.
 * - A monotonic attempt counter additionally fences late responses that
 *   belong to the SAME generation but were superseded by a newer probe.
 *
 * Consequences (fail closed):
 * - A stale generation result can never repopulate authority.
 * - A late success/error from an older attempt can never repopulate authority.
 * - A permission change (re-confirmed identity, even with identical ids)
 *   bumps the generation, invalidating everything captured before it.
 *
 * This file deliberately implements NO later W01b behaviour (no CSRF
 * replay semantics) — it is only the fencing seam PLUS the W01c scoped-key
 * primitive and caller-callback fencer built directly on it.
 */

export interface IdentityScopeState {
  /** Confirmed user id of the current generation, or null when unconfirmed. */
  userId: string | null;
  /** Confirmed workspace (org) id of the current generation, or null. */
  workspaceId: string | null;
  /** Monotonically increasing generation. Changes on every confirmed identity (incl. permission changes). */
  generation: number;
}

export interface IdentityScope {
  /** Current generation state (identity captured + generation number). */
  current(): IdentityScopeState;
  /** True when `generation` is still the live generation. */
  isCurrent(generation: number): boolean;
  /**
   * Commit a new confirmed identity: bump the generation, capture the ids,
   * abort everything registered under older generations and notify
   * invalidation listeners. Called on EVERY confirmed identity change —
   * including a re-confirmation of the same user/workspace (permission
   * change invalidation) — and on transitions that end the old identity
   * (logout, login starting verification).
   */
  advance(userId: string | null, workspaceId: string | null): IdentityScopeState;
  /** Register an abort callback for in-flight work owned by `generation`. Returns unregister. */
  registerAbort(generation: number, abort: () => void): () => void;
  /** Subscribe to generation transitions. Returns unsubscribe. */
  onInvalidate(listener: (prev: IdentityScopeState, next: IdentityScopeState) => void): () => void;
  /** Open a fence window; only the newest attempt may commit its result. */
  beginAttempt(): number;
  /** True when `attempt` is still the newest opened attempt. */
  isCurrentAttempt(attempt: number): boolean;
}

export function createIdentityScope(): IdentityScope {
  let generation = 0;
  let state: IdentityScopeState = { userId: null, workspaceId: null, generation };
  let latestAttempt = 0;
  const aborts = new Map<number, Set<() => void>>();
  const listeners = new Set<(prev: IdentityScopeState, next: IdentityScopeState) => void>();

  function abortOlderGenerations(live: number): void {
    for (const [gen, callbacks] of aborts) {
      if (gen === live) continue;
      for (const abort of callbacks) abort();
      aborts.delete(gen);
    }
  }

  return {
    current: () => state,

    isCurrent: (generation: number) => generation === state.generation,

    advance(userId, workspaceId) {
      const prev = state;
      state = { userId, workspaceId, generation: generation + 1 };
      generation = state.generation;
      // NOTE: the attempt counter is deliberately NOT reset here — it is
      // monotonic forever, so a late response from an old attempt can never
      // collide with a new attempt id; transitions are fenced by generation.
      abortOlderGenerations(state.generation);
      for (const listener of listeners) listener(prev, state);
      return state;
    },

    registerAbort(generation, abort) {
      let set = aborts.get(generation);
      if (!set) {
        set = new Set();
        aborts.set(generation, set);
      }
      set.add(abort);
      return () => {
        set.delete(abort);
        if (set.size === 0) aborts.delete(generation);
      };
    },

    onInvalidate(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    beginAttempt: () => ++latestAttempt,

    isCurrentAttempt: (attempt: number) => attempt === latestAttempt,
  };
}

/* ─────────────────────────────────────────────────────────────────────── */
/* APP-W01c — scoped-query key factory + caller-callback fencing            */
/* ─────────────────────────────────────────────────────────────────────── */

/**
 * Raw identity tuple carried by every scoped private query key. The tuple
 * is placed in FRONT of the logical key Threefolds:
 * - two identities never share cache entries (same user/different
 *   workspace, same user/changed permissions, full user switch);
 * - every key is already nested under its identity when it is created, so
 *   a transition can remove the whole subtree with ONE prefix cancel;
 * - prefetch/cancel callers cannot cross identity boundaries even by
 *   accident — a stale prefix matches nothing.
 *
 * A permission change GENERATES A NEW generation number, so a
 * re-confirmed identity with identical ids produces new scoping tuples
 * and thus fresh cache state (no repopulation from the old permission
 * set is reachable by any key).
 */
export type ScopedIdentityTuple = readonly [
  userId: string,
  workspaceId: string,
  generation: number,
];

/**
 * Build the scoping tuple for the current confirmed identity. Returns null
 * when no identity is confirmed (queries must not run before an
 * authenticated, current identity — fail closed).
 */
export function currentScopingTuple(scope: IdentityScope): ScopedIdentityTuple | null {
  const { userId, workspaceId, generation } = scope.current();
  if (!userId || !workspaceId) return null;
  return [userId, workspaceId, generation] as const;
}

/**
 * Prefix a logical key with the CURRENT confirmed identity tuple. Keys are
 * scoped at CREATION time; a tuple that looked current at creation stays
 * fenced by the generation component even if the identity later changes —
 * a superseded prefix can never match the new identity's cache subtree.
 */
export function scopeQueryKey(scope: IdentityScope, logical: readonly unknown[]): readonly unknown[] | null {
  const tuple = currentScopingTuple(scope);
  if (!tuple) return null;
  return [...tuple, ...logical];
}

/**
 * Fence a caller-supplied mutate callback: the returned function runs its
 * side effect ONLY if the scoped generation it was created under is STILL
 * current. A stale callback (identity transitioned away from this
 * generation, its permissions re-confirmed, or the identity ended) can
 * never navigate, toast or alter global selections. The caller receives a
 * clear classification: the wrapped function resolves to `undefined`
 * without invoking the body.
 *
 * Callbacks are captured WITH their creating generation; the generation is
 * re-checked at every invocation, not just at capture time.
 */
export function fenceMutateCallback<C extends (...args: never[]) => unknown>(
  scope: IdentityScope,
  generation: number,
  callback: C
): (...args: Parameters<C>) => void {
  return (...args: Parameters<C>): void => {
    if (!scope.isCurrent(generation)) return;
    callback(...args);
  };
}

/**
 * The app-wide IdentityScope singleton for the query seam. The query
 * modules (scout-queries.ts / queries.ts) share THIS instance, so cache
 * scoping and key creation always consult one confirmed identity catalogue.
 * The React-side synchroniser (useSyncIdentityScope) advances it only on
 * server-confirmed commits; main.tsx's cache bridge listens for transitions
 * and fence-cancels/removes the departing generation's subtree.
 */
export const appIdentityScope: IdentityScope = createIdentityScope();

/* ─────────────────────────────────────────────────────────────────────── */
/* W01d-D1 correction — ONE confirmed-identity transition authority         */
/*                                                                          */
/* The AuthProvider commits identity against THIS singleton, so a toast /   */
/* cache / consumer generation can never drift from the confirmed identity  */
/* state. collapseIdentity() is the provider-side seam for the transitions  */
/* that END an identity (logout, login reset, session-expired outcome):     */
/* they advance to a null identity exactly once.                            */
/* ─────────────────────────────────────────────────────────────────────── */

/** Collapse the confirmed identity (null ids, new generation). */
export function collapseIdentity(): void {
  appIdentityScope.advance(null, null);
}

/** Commit a server-CONFIRMED identity onto the shared scope (new generation). */
export function confirmIdentity(
  userId: string,
  workspaceId: string
): IdentityScopeState {
  return appIdentityScope.advance(userId, workspaceId);
}

/* ─────────────────────────────────────────────────────────────────────── */
/* W01c-C1/C2 correction — mutation ADMISSION capture                       */
/* ─────────────────────────────────────────────────────────────────────── */

/**
 * The admission snapshot a mutation captures when its REQUEST starts. The
 * scoped key is derived from the ADMISSION identity tuple — never from the
 * settled-time identity — so a stale A callback can write at most under A's
 * own (already-departed) key, and the isCurrent fence makes even that
 * impossible. Stale callbacks perform ZERO cache mutation under B.
 */
export interface MutationAdmission {
  generation: number;
  /** True only while the admitted identity (user+workspace+generation) is still the live confirmed identity. */
  isCurrent(): boolean;
  /** The ADMISSION-scoped key for a logical key (NOT a re-resolved current-identity key). */
  key(logical: readonly unknown[]): readonly unknown[];
}

/**
 * Capture the mutation admission at REQUEST (wire) time. Returns null when
 * no identity was confirmed when the mutation was admitted — in that case
 * the mutation is rejected before the wire (sessionRequiredError path).
 */
export function captureMutationAdmission(
  scope: IdentityScope
): MutationAdmission | null {
  const tuple = currentScopingTuple(scope);
  if (!tuple) return null;
  const [userId, workspaceId, generation] = tuple;
  const admission: MutationAdmission = {
    generation,
    isCurrent() {
      const current = scope.current();
      return (
        current.generation === generation &&
        current.userId === userId &&
        current.workspaceId === workspaceId
      );
    },
    key(logical: readonly unknown[]): readonly unknown[] {
      return [...tuple, ...logical];
    },
  };
  return admission;
}

/* ─────────────────────────────────────────────────────────────────────── */
/* W01d-D2 correction — STRUCTURAL purge predicate                          */
/* ─────────────────────────────────────────────────────────────────────── */

/**
 * Does this cache key belong to the DEPARTED identity/generation?
 *
 * Structural matching on the scoped-key layout
 * `[userId, workspaceId, generation, ...logical]` — never a JSON substring
 * search. A key is the departing generation's private subtree when its
 * scoping tuple STRUCTURALLY EQUALS the departed identity tuple:
 *
 * - same user+workspace, generation-only advance (permission change):
 *   purged via the departed generation component (old-gen keys match, the
 *   new generation's identical-id keys do not);
 * - A → B: purged via equality — B's keys match no departed component;
 * - A → unauthenticated (logout): the listener receives the PRE-collapse
 *   state (user_a, ws_a, gen), so equality purges exactly the departed
 *   user's subtree and nothing else.
 */
export function isDepartedPrivateKey(
  key: readonly unknown[],
  departed: IdentityScopeState
): boolean {
  return (
    key[0] === departed.userId &&
    key[1] === departed.workspaceId &&
    key[2] === departed.generation
  );
}

/**
 * React-side helper (importable by query modules): synchronise the app
 * IdentityScope with the CONFIRMED identity. Advancing is idempotent for an
 * identical tuple (same ids AND same generation -> no-op), so re-renders do
 * not bump the generation; only a genuinely new confirmed commit does.
 *
 * identity-scope.ts deliberately holds NO React imports; the hook wrapper
 * lives with the query seam that consumes it (scout-queries.ts / queries.ts).
 */
export function useSyncIdentityScope(
  user: { userId: string } | null,
  org: { orgId: string } | null
): void {
  const scope = appIdentityScope;
  const current = scope.current();
  if (user && org) {
    if (current.userId !== user.userId || current.workspaceId !== org.orgId) {
      scope.advance(user.userId, org.orgId);
    }
  }
  // Deliberately NO collapse-to-null advance here: a logout/null identity is
  // driven by the transition invalidation (advance(null,null)) from the same
  // seam in the gateway, not by query-module re-renders (which run too late).
}
